<template>
  <b-modal
    v-model="show_modal"
    :can-cancel="current_profile_true.type !== PROFILE_TYPES.CUSTOMER"
    :cancelCallback="() => $emit('close')"
  >
    <SurveyIntroModal
      v-if="show_intro_modal"
      :survey_type="survey_type_current_survey"
      @close="show_intro_modal = false"
    />
    <Modal
      v-else-if="show_surveys_modal && question_currently_shown"
      :data-resource-id="question_currently_shown.attributes.survey_id"
      :data-resource-type="RECORD_TYPE.SURVEY"
      data-test="survey-popup-modal"
      :is_cancel="false"
      :is_confirmed="is_confirmed"
      :is_show_header="false"
      :is_small="false"
      :prevent_enter_shortcut="true"
      @actionPrimary="updateQuestion"
      @actionSecondary="updateQuestion(SurveyQuestion.STATUS_ANSWER_LATER)"
    >
      <template #content>
        <SurveyForm
          :question_number="question_currently_shown_number"
          :question_type="question_currently_shown.attributes.question_type"
          :questions="survey_type_current_survey.attributes.questions_count"
          :fixed_options="question_currently_shown.attributes.fixed_options"
          style="width: 100%"
          :title="question_currently_shown.attributes.title"
          @feedbackUpdated="feedbackUpdated"
          @scoreSelected="scoreSelected"
        />
      </template>
      <template #ButtonPrimary>
        {{ $t('survey.actions.submit') }}
      </template>
      <template #ButtonSecondary>
        {{ $t('survey.labels.answer_later') }}
      </template>
      <template
        v-if="current_question_optional"
        #content-footer
      >
        <ButtonTertiary
          data-test="modal-confirm-tertiary"
          @onClick="() => updateQuestion(SurveyQuestion.STATUS_NO_OPINION)"
        >
          {{ $t('survey.labels.no_opinion') }}
        </ButtonTertiary>
      </template>
    </Modal>
    <ThankYouModal
      v-else-if="show_thank_you_modal"
      :client_name="current_client.attributes.name"
      @close="$emit('close')"
    />
  </b-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import Modal from '@/app/shared_components/modals/TheModal.vue';
import SurveyForm from '@/app/shared_components/surveys/SurveyForm.vue';
import { PROFILE_TYPES, RECORD_TYPE, SurveyQuestion } from '@/app/data/model_constants';
import useActivityNotification from '@/app/composables/useNotificiationOpen';
import ThankYouModal from '@/app/shared_components/surveys/TheThankYouModal.vue';
import SurveyIntroModal from '@/app/shared_components/surveys/SurveyIntroModal.vue';
import { isEmptyHTML } from '@/app/util/editor/editor';

const props = defineProps({
  project_id: { type: String, required: true },
  survey_id: { type: String, default: null },
});

const emit = defineEmits(['close']);

const { current_profile_true, openSurveyNotificationsExplicit, openSurveyActivitiesImplicit } =
  useActivityNotification();

const { current_client, survey_questions, survey_types } = mapGetters([
  'current_client',
  'survey_questions',
  'survey_types',
]);
const { FETCH_SURVEYS, UPDATE_SURVEY_QUESTION } = mapActions(['FETCH_SURVEYS', 'UPDATE_SURVEY_QUESTION']);

const is_confirmed = ref(false);
const show_surveys_modal = ref(false);
const show_thank_you_modal = ref(false);
const show_intro_modal = ref(false);
const questions_queue = ref([]);
const surveys = ref([]);

const show_modal = computed({
  set() {
    return true;
  },
  get() {
    return show_intro_modal.value || show_surveys_modal.value || show_thank_you_modal.value;
  },
});

const question_currently_shown = computed(() => _.first(questions_queue.value));

const survey_currently_shown = computed(() => {
  if (question_currently_shown.value) {
    return _.find(surveys.value, ['id', question_currently_shown.value.attributes.survey_id]);
  }
  return undefined;
});

const survey_type_current_survey = computed(() => {
  if (survey_currently_shown.value) {
    return _.find(survey_types.value, ['id', survey_currently_shown.value.attributes.survey_type_id]);
  }
  return undefined;
});

const current_question_optional = computed(() => {
  if (survey_type_current_survey.value) {
    return !survey_type_current_survey.value.attributes.obligatory;
  }
  return undefined;
});

const question_currently_shown_number = computed(() => {
  if (survey_type_current_survey.value) {
    return (
      survey_type_current_survey.value.attributes.questions_count -
      _.filter(questions_queue.value, ['attributes.survey_type_id', survey_type_current_survey.value.id]).length +
      1
    );
  }
  return undefined;
});

watch(
  () => _.get(survey_currently_shown.value, 'id'),
  () => {
    if (survey_currently_shown.value && !isEmptyHTML(survey_type_current_survey.value.attributes.description_html)) {
      show_intro_modal.value = true;
    }
  },
  { immediate: true },
);

function setUpSurveysQueue() {
  const unresponded = new Set([SurveyQuestion.STATUS_UNANSWERED, SurveyQuestion.STATUS_ANSWER_LATER]);
  const survey_ids = props.survey_id ? [props.survey_id] : _.map(surveys.value, 'id');
  const questions = _.chain(survey_questions.value)
    .filter((q) => survey_ids.includes(q.attributes.survey_id))
    .filter((q) => unresponded.has(q.attributes.status))
    .groupBy('attributes.survey_type_id')
    .map((qs) => _.chain(qs).groupBy('attributes.survey_id').values().first().value())
    .flatten()
    .value();

  _.each(questions, (survey_question) => {
    questions_queue.value.push({
      id: survey_question.id,
      attributes: {
        title: survey_question.attributes.title,
        question_type: survey_question.attributes.question_type,
        fixed_options: survey_question.attributes.fixed_options,
        survey_id: survey_question.attributes.survey_id,
        survey_type_id: survey_question.attributes.survey_type_id,
        survey_answer_attributes: {},
      },
    });
  });
}

function feedbackUpdated(val) {
  question_currently_shown.value.attributes.survey_answer_attributes.answer = val;
}

function scoreSelected(val) {
  question_currently_shown.value.attributes.survey_answer_attributes.score = val;
  is_confirmed.value =
    question_currently_shown.value.attributes.question_type === SurveyQuestion.OPEN_ENDED
      ? Boolean(question_currently_shown.value.attributes.survey_answer_attributes.answer)
      : true;
}

async function updateQuestion(status = SurveyQuestion.STATUS_ANSWERED) {
  const is_answered = status === SurveyQuestion.STATUS_ANSWERED;
  const survey_ids = _.chain(surveys.value)
    .filter(['attributes.survey_type_id', question_currently_shown.value.attributes.survey_type_id])
    .map('id')
    .value();
  const survey_questions_matching = _.chain(survey_questions.value)
    .filter((q) => survey_ids.includes(q.attributes.survey_id))
    .filter((q) => q.attributes.title === question_currently_shown.value.attributes.title)
    .value();

  await Promise.all(
    _.map(survey_questions_matching, async (question) => {
      const record = _.cloneDeep(question);
      record.attributes.status = status;
      if (is_answered) {
        const { score, answer } = question_currently_shown.value.attributes.survey_answer_attributes;
        record.attributes.survey_answer_attributes = { score, answer };
      }
      await UPDATE_SURVEY_QUESTION({ record });
    }),
  );

  if (status !== SurveyQuestion.STATUS_ANSWER_LATER) openSurveyNotificationsExplicit(survey_questions_matching);
  if (is_answered) show_thank_you_modal.value = true;
  questions_queue.value =
    status === SurveyQuestion.STATUS_ANSWER_LATER
      ? []
      : _.filter(questions_queue.value, (e) => e.id !== question_currently_shown.value.id);
  if (_.isEmpty(questions_queue.value) && !show_thank_you_modal.value) emit('close');
  is_confirmed.value = false;
}

async function initialize() {
  surveys.value = await FETCH_SURVEYS({ project_id: props.project_id, answered: false });
  if (_.isEmpty(surveys.value) && !props.survey_id) {
    emit('close');
    return;
  }
  setUpSurveysQueue();
  show_surveys_modal.value = true;
  openSurveyActivitiesImplicit(surveys.value);
}

initialize();
</script>
